import React, { useEffect, useRef } from 'react';

interface TorchDef {
    ox: number; oy: number;
    ax: number; ay: number;
    px: number; py: number;
    sx: number; sy: number;
    r:  number;
    b:  number;
}

const TORCH_DEFS: TorchDef[] = [
    { ox: 0.22, oy: 0.42, ax: 0.20, ay: 0.28, px: 0.00, py: 1.57, sx: 1.20, sy: 0.82, r: 520, b: 0.78 },
    { ox: 0.60, oy: 0.58, ax: 0.22, ay: 0.22, px: 2.20, py: 0.00, sx: 0.95, sy: 1.18, r: 480, b: 0.72 },
    { ox: 0.80, oy: 0.33, ax: 0.14, ay: 0.30, px: 4.10, py: 3.14, sx: 1.55, sy: 0.76, r: 590, b: 0.80 },
    { ox: 0.40, oy: 0.74, ax: 0.26, ay: 0.16, px: 1.25, py: 4.40, sx: 1.05, sy: 1.38, r: 500, b: 0.70 },
];

type Pt = { x: number; y: number };

const AnimatedBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animId: number;
        let t = 0;

        const HEX_R       = 155;
        const SQRT3       = Math.sqrt(3);
        const COL_W       = SQRT3 * HEX_R;   // ~268 px
        const ROW_H       = 1.5  * HEX_R;    //  232 px
        const DR          = HEX_R - 4;       // drawing radius  (~6 px gap)
        const EXTRUDE = 40;
        const ROT     = 0;//-25 * Math.PI / 180;

        let nCols = 0, nRows = 0;
        let mouseCanvasX = -99999;
        let mouseCanvasY = -99999;
        let hoveredCol   = -99999;
        let hoveredRow   = -99999;
        let hoverProg    = 0;   // 0 → 1, animates at moderate speed

        const init = () => {
            const p = canvas.parentElement;
            const pw = p ? p.clientWidth  : window.innerWidth;
            const ph = p ? p.clientHeight : window.innerHeight;
            canvas.width  = Math.ceil(pw * 1.20);
            canvas.height = Math.ceil(ph * 1.20);
            nCols = Math.ceil(canvas.width  / COL_W) + 5;
            nRows = Math.ceil(canvas.height / ROW_H) + 5;
        };

        init();
        window.addEventListener('resize', init);

        // Convert a DOM mouse event to canvas-local pixel coords,
        // undoing the CSS rotation (-5°) + offset/size of the canvas element.
        const toCanvas = (clientX: number, clientY: number) => {
            const p = canvas.parentElement;
            if (!p) return null;
            const rect = p.getBoundingClientRect();
            const pw = rect.width, ph = rect.height;

            // Mouse relative to the centre of .projects (= centre of the canvas in CSS space)
            const dx = (clientX - rect.left) - pw * 0.5;
            const dy = (clientY - rect.top)  - ph * 0.5;

            // Undo the -5° CSS rotation  →  apply +5°
            const cos5 = Math.cos(0);//-ROT);   // cos(+5°)
            const sin5 = Math.sin(0);//-ROT);   // sin(+5°)
            const rx = dx * cos5 + dy * sin5;
            const ry = -dx * sin5 + dy * cos5;

            // Scale: canvas CSS size = 1.2 × parent, pixel buffer ≈ same ratio
            const sx = canvas.width  / (pw * 1.2);
            const sy = canvas.height / (ph * 1.2);

            return { x: rx * sx + canvas.width * 0.5, y: ry * sy + canvas.height * 0.5 };
        };

        // Nearest hex cell for a canvas-local point
        const nearest = (px: number, py: number) => {
            const rr = Math.round(py / ROW_H);
            const rc = Math.round((px - (rr % 2 !== 0 ? COL_W / 2 : 0)) / COL_W);
            let best = Infinity, bc = rc, br = rr;
            for (let dr = -1; dr <= 1; dr++) {
                for (let dc = -2; dc <= 2; dc++) {
                    const r = rr + dr, c = rc + dc;
                    const cx = c * COL_W + (r % 2 !== 0 ? COL_W / 2 : 0);
                    const cy = r * ROW_H;
                    const d  = (px - cx) ** 2 + (py - cy) ** 2;
                    if (d < best) { best = d; bc = c; br = r; }
                }
            }
            return { col: bc, row: br };
        };

        const onMove = (e: MouseEvent) => {
            const c = toCanvas(e.clientX, e.clientY);
            if (c) { mouseCanvasX = c.x; mouseCanvasY = c.y; }
        };
        const onLeave = () => { mouseCanvasX = -99999; mouseCanvasY = -99999; };

        const parent = canvas.parentElement;
        parent?.addEventListener('mousemove', onMove);
        parent?.addEventListener('mouseleave', onLeave);

        // ── Drawing helpers ────────────────────────────────────────────────
        const verts = (cx: number, cy: number, r: number): Pt[] =>
            Array.from({ length: 6 }, (_, i) => {
                const a = (Math.PI / 3) * i + Math.PI / 6;
                return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
            });

        const tracePath = (pts: Pt[]) => {
            ctx.beginPath();
            pts.forEach((v, i) => (i === 0 ? ctx.moveTo(v.x, v.y) : ctx.lineTo(v.x, v.y)));
            ctx.closePath();
        };

        const fillHex = (cx: number, cy: number, r: number, style: string) => {
            tracePath(verts(cx, cy, r));
            ctx.fillStyle = style;
            ctx.fill();
        };

        // ── Animation loop ─────────────────────────────────────────────────
        const animate = () => {
            t += 0.007; // ← animation speed: raise to go faster, lower to go slower

            // Update hover state
            const inSection = mouseCanvasX > 0 && mouseCanvasX < canvas.width &&
                              mouseCanvasY > 0 && mouseCanvasY < canvas.height;
            if (inSection) {
                const { col, row } = nearest(mouseCanvasX, mouseCanvasY);
                if (col !== hoveredCol || row !== hoveredRow) {
                    hoveredCol = col;
                    hoveredRow = row;
                    // carry hoverProg over so transition is smooth when sliding between cells
                }
                hoverProg = Math.min(1, hoverProg + 0.006);
            } else {
                hoverProg = Math.max(0, hoverProg - 0.005);
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Rotate the canvas context by −5° around its centre
            ctx.save();
            ctx.translate(canvas.width * 0.5, canvas.height * 0.5);
            ctx.rotate(ROT);
            ctx.translate(-canvas.width * 0.5, -canvas.height * 0.5);

            // ── 1. Torch glows (behind hex cells) ─────────────────────────
            TORCH_DEFS.forEach(td => {
                const tx = (td.ox + Math.sin(t * td.sx + td.px) * td.ax) * canvas.width;
                const ty = (td.oy + Math.sin(t * td.sy + td.py) * td.ay) * canvas.height;
                const g  = ctx.createRadialGradient(tx, ty, 0, tx, ty, td.r);
                g.addColorStop(0,    `rgba(155, 225, 255, ${td.b})`);
                g.addColorStop(0.28, `rgba(100, 188, 250, ${td.b * 0.42})`);
                g.addColorStop(0.62, `rgba(62,  148, 232, ${td.b * 0.10})`);
                g.addColorStop(1,    `rgba(38,  108, 208, 0)`);
                ctx.beginPath();
                ctx.arc(tx, ty, td.r, 0, Math.PI * 2);
                ctx.fillStyle = g;
                ctx.fill();
            });

            // ── 2. Hex grid ────────────────────────────────────────────────
            for (let row = -3; row <= nRows; row++) {
                for (let col = -3; col <= nCols; col++) {
                    const cx = col * COL_W + (row % 2 !== 0 ? COL_W / 2 : 0);
                    const cy = row * ROW_H;
                    if (cx < -HEX_R * 3 || cx > canvas.width  + HEX_R * 3) continue;
                    if (cy < -HEX_R * 3 || cy > canvas.height + HEX_R * 3) continue;

                    // Independent Z oscillation per cell
                    const seed  = ((col * 127 + row * 31) >>> 0) & 511;
                    const freq  = 0.18 + (seed % 13) * 0.055;
                    const phase = (seed / 512) * Math.PI * 2;
                    const zRaw  = Math.sin(t * freq + phase);  // −1 → +1

                    // Hover blends this cell's z toward +1 through the same animation path
                    const isHovered = inSection && col === hoveredCol && row === hoveredRow;
                    const z    = isHovered ? zRaw + (1 - zRaw) * hoverProg * 0.65 : zRaw;
                    const brt  = (z + 1) * 0.5;
                    const extr = Math.max(0, z) * EXTRUDE;

                    fillHex(cx, cy, DR, 'rgba(14, 23, 36, 0.97)');

                    if (extr > 0.8) {
                        const fv = verts(cx, cy - extr, DR);
                        const bv = verts(cx, cy,         DR);
                        [5, 0, 1, 2].forEach(i => {
                            const j = (i + 1) % 6;
                            tracePath([fv[i], fv[j], bv[j], bv[i]]);
                            ctx.fillStyle = `rgba(6, 13, 22, ${0.36 + z * 0.24})`;
                            ctx.fill();
                        });
                    }

                    const rc = Math.round(12 + brt * 26);
                    const gc = Math.round(20 + brt * 32);
                    const bc = Math.round(34 + brt * 44);
                    fillHex(cx, cy - extr, DR, `rgba(${rc}, ${gc}, ${bc}, 0.96)`);

                    tracePath(verts(cx, cy - extr, DR));
                    ctx.strokeStyle = `rgba(108, 188, 235, ${0.015 + brt * 0.045})`;
                    ctx.lineWidth = 0.5;
                    ctx.stroke();
                }
            }

            ctx.restore();
            animId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', init);
            cancelAnimationFrame(animId);
            parent?.removeEventListener('mousemove', onMove);
            parent?.removeEventListener('mouseleave', onLeave);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top:    '-10%',
                left:   '-10%',
                width:  '120%',
                height: '120%',
                display: 'block',
                pointerEvents: 'none',
                zIndex: 1,
                WebkitMaskImage:
                    'linear-gradient(185deg, transparent 0%, black 28%, black 72%, transparent 100%)',
                maskImage:
                    'linear-gradient(185deg, transparent 0%, black 28%, black 72%, transparent 100%)',
            }}
        />
    );
};

export default AnimatedBackground;
